import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import { getWhitespace, scanWhitespace } from '../api';
import { tileUrl } from '../theme';

// "Where to open next" — the ranked whitespace shortlist on a map. The scan
// runs as a background job; this polls its status and renders the saved
// candidates once they land.
const fmtR = (v) => v == null ? '—' : 'R' + Math.round(v).toLocaleString('en-ZA');

export default function WhitespacePanel({ category, enabled }) {
  const [data, setData] = useState(null);        // { candidates, job, run_id, generated }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);

  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markers = useRef({});
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await getWhitespace(category);
      setData(d);
      return d;
    } catch (e) { setErr(e.message); return null; }
  }, [category]);

  useEffect(() => { if (enabled) load(); return () => clearInterval(pollRef.current); }, [enabled, load]);

  const cands = data?.candidates || [];
  const job = data?.job;
  const scanning = job && (job.status === 'queued' || job.status === 'running');

  // poll while a scan is in flight
  useEffect(() => {
    clearInterval(pollRef.current);
    if (scanning) {
      pollRef.current = setInterval(load, 4000);
      return () => clearInterval(pollRef.current);
    }
  }, [scanning, load]);

  const runScan = async () => {
    setBusy(true); setErr(null);
    try { await scanWhitespace(category); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // init map once
  useEffect(() => {
    if (!enabled || mapObj.current || !mapRef.current) return;
    mapObj.current = L.map(mapRef.current, { center: [-29.0, 25.0], zoom: 5, zoomControl: true, scrollWheelZoom: false });
    const tiles = L.tileLayer(tileUrl(),
      { attribution: '© OpenStreetMap © CARTO', subdomains: 'abcd', maxZoom: 19 }).addTo(mapObj.current);
    const onTheme = () => tiles.setUrl(tileUrl());
    window.addEventListener('themechange', onTheme);
    return () => window.removeEventListener('themechange', onTheme);
  }, [enabled]);

  // render markers when candidates change
  useEffect(() => {
    const map = mapObj.current;
    if (!map) return;
    Object.values(markers.current).forEach(m => m.remove());
    markers.current = {};
    if (!cands.length) return;

    const maxNet = Math.max(...cands.map(c => c.net_opportunity || 0)) || 1;
    cands.forEach(c => {
      const t = (c.net_opportunity || 0) / maxNet;              // 0..1 strength
      const color = t > 0.66 ? '#ff6a3d' : t > 0.33 ? '#ffb02e' : '#7db8ff';
      const icon = L.divIcon({
        html: `<div style="width:24px;height:24px;border-radius:50%;background:${color};
          border:2px solid var(--s-solid);color:#fff;font:700 11px/24px system-ui;text-align:center;
          box-shadow:0 0 8px rgba(0,0,0,0.4)">${c.rank}</div>`,
        iconSize: [24, 24], iconAnchor: [12, 12], className: '',
      });
      const m = L.marker([c.lat, c.lon], { icon }).addTo(map).bindPopup(`
        <div style="font-size:12px;min-width:170px">
          <strong>#${c.rank} · net ${fmtR(c.net_opportunity)}/wk</strong><br>
          Predicted ${fmtR(c.predicted_weekly_sales)}/wk<br>
          <span style="color:#8e919b">
            ${fmtR(c.low)}–${fmtR(c.high)} · ${c.households ? c.households.toLocaleString() + ' HH · ' : ''}
            ${c.nearest_own_km != null ? c.nearest_own_km + ' km to nearest store · ' : ''}
            ${Math.round((c.cannibalization_pct || 0) * 100)}% overlap
          </span>
        </div>`);
      markers.current[c.rank] = m;
    });
    map.fitBounds(L.latLngBounds(cands.map(c => [c.lat, c.lon])).pad(0.2));
  }, [cands]);

  const focus = (c) => {
    setSelected(c.rank);
    const map = mapObj.current, m = markers.current[c.rank];
    if (map && m) { map.flyTo([c.lat, c.lon], Math.max(map.getZoom(), 11), { duration: 0.7 }); m.openPopup(); }
  };

  if (!enabled) return null;

  return (
    <div style={S.card}>
      <div style={S.head}>
        <div>
          <div style={S.title}>Where to open next</div>
          <div style={S.sub}>
            {data?.generated
              ? `Ranked opportunities · scanned ${new Date(data.generated).toLocaleDateString('en-ZA')}`
              : 'Score the demand grid against your model and footprint to rank open-store opportunities.'}
          </div>
        </div>
        <button className="sq-btn primary" onClick={runScan} disabled={busy || scanning}>
          {scanning ? 'Scanning…' : busy ? 'Queuing…' : cands.length ? 'Rescan' : 'Run scan'}
        </button>
      </div>

      {scanning && <div style={S.note}><span style={S.dot} />Scan {job.status} — a worker is ranking candidates. This can take a few minutes.</div>}
      {job?.status === 'failed' && <div style={S.err}>Last scan failed: {job.error || 'unknown error'}</div>}
      {err && <div style={S.err}>{err}</div>}

      {cands.length > 0 && (
        <>
          <div ref={mapRef} style={S.map} />
          <div style={S.list}>
            {cands.map(c => (
              <div key={c.rank} style={{ ...S.row, ...(selected === c.rank ? S.rowOn : {}) }} onClick={() => focus(c)}>
                <span style={S.rank}>{c.rank}</span>
                <span style={S.net}>{fmtR(c.net_opportunity)}<small style={S.per}>/wk net</small></span>
                <span style={S.meta}>pred {fmtR(c.predicted_weekly_sales)}</span>
                <span style={S.meta}>{c.nearest_own_km != null ? `${c.nearest_own_km} km` : 'no store near'}</span>
                <span style={S.meta}>{Math.round((c.cannibalization_pct || 0) * 100)}% overlap</span>
              </div>
            ))}
          </div>
        </>
      )}

      {!cands.length && !scanning && (
        <div style={S.empty}>No shortlist yet. Run a scan to rank where your model says to open next.</div>
      )}
    </div>
  );
}

const S = {
  card: { border: '1px solid var(--s-line)', borderRadius: 14, padding: '18px 20px', marginBottom: 16, background: 'var(--s-w02)' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 },
  title: { fontFamily: 'var(--s-serif)', fontSize: 18, fontWeight: 700, color: 'var(--s-text)' },
  sub: { fontSize: 12.5, color: 'var(--s-muted)', marginTop: 4, maxWidth: 460 },
  note: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--s-amber)', marginBottom: 12 },
  dot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--s-amber)', animation: 'pulse 1.4s infinite' },
  err: { fontSize: 12.5, color: 'var(--s-red)', marginBottom: 10 },
  map: { height: 380, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--s-line)', marginBottom: 12 },
  list: { display: 'flex', flexDirection: 'column', gap: 2 },
  row: { display: 'grid', gridTemplateColumns: '28px 1.2fr 1fr 1fr 1fr', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', borderBottom: '1px solid var(--s-line2)' },
  rowOn: { background: 'rgba(255,106,61,0.1)' },
  rank: { font: '700 12px var(--s-mono)', color: 'var(--s-accent)' },
  net: { font: '600 13px var(--s-mono)', color: 'var(--s-text)' },
  per: { color: 'var(--s-faint)', fontWeight: 400, fontSize: 10 },
  meta: { fontSize: 12, color: 'var(--s-muted)', fontFamily: 'var(--s-mono)', whiteSpace: 'nowrap' },
  empty: { fontSize: 13, color: 'var(--s-faint)', padding: '8px 0' },
};
